import { Autocomplete, Grid, TextField, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { getTransportContentSx, getTransportLabelSx } from './transportTypographySx';
import YesNoToggle from './YesNoToggle';
import { getOutsourceCompaniesByFacility, getDrivers, getFacilityVehicles } from '../../../../api/transports';
import useFetchList from '../../../../hooks/useFetchList';
import { FIELDS } from '../constants';
import { labelSort } from './utils';

OutsourceSection.propTypes = {
  outsourceState: PropTypes.shape({
    isOutsourced: PropTypes.bool,
    outsourceCompanyId: PropTypes.number,
    vehicleId: PropTypes.number,
    assignedDriverId: PropTypes.number,
    facilityId: PropTypes.number,
    transportType: PropTypes.string,
  }).isRequired,
  setIsOutsourced: PropTypes.func.isRequired,
  setter: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  showTransportType: PropTypes.bool,
  detailsMode: PropTypes.bool,
};

export default function OutsourceSection({
  outsourceState,
  setIsOutsourced,
  setter,
  disabled = false,
  showTransportType = false,
  detailsMode = false,
}) {
  const {
    isOutsourced,
    outsourceCompanyId,
    vehicleId,
    assignedDriverId,
    facilityId,
    transportType = null,
  } = outsourceState;

  const autocompleteRowProps = showTransportType ? { xs: 12 } : {};

  // Outsource Companies
  const [outsourceCompanies, outsourceLoading] = useFetchList(
    getOutsourceCompaniesByFacility,
    [isOutsourced, facilityId],
    isOutsourced && facilityId ? [facilityId] : [null],
    labelSort
  );

  // Vehicles
  const [vehicles, vehiclesLoading] = useFetchList(
    getFacilityVehicles,
    [facilityId, isOutsourced],
    facilityId && isOutsourced === false ? [facilityId] : [null],
    labelSort
  );

  // Drivers
  const [drivers, driversLoading] = useFetchList(
    getDrivers,
    [facilityId, isOutsourced],
    facilityId && isOutsourced === false ? [facilityId] : [null],
    labelSort
  );

  return (
    <Grid container spacing={2} alignItems="center">
      <Grid item>
        <YesNoToggle
          title="Outsourced"
          setter={setIsOutsourced}
          value={isOutsourced}
          disabled={disabled}
          detailsMode={detailsMode}
        />
      </Grid>
      {showTransportType && (
        <Grid item sx={{ ml: 1.5, display: 'flex', alignItems: 'center' }}>
          <Typography color="text.secondary" sx={getTransportLabelSx(detailsMode)}>
            Transport Type:
            <Typography component="span" sx={{ ...getTransportContentSx(detailsMode), ml: 1.25 }}>
              {transportType}
            </Typography>
          </Typography>
        </Grid>
      )}
      {isOutsourced && (
        <Grid item {...autocompleteRowProps}>
          <Grid container columnSpacing={2} rowSpacing={1}>
            <Grid item>
              <Autocomplete
                sx={{ minWidth: 350 }}
                size="small"
                value={outsourceCompanies?.find((c) => c.value === outsourceCompanyId) ?? null}
                onChange={(_, newValue) => setter(FIELDS.OUTSOURCE_COMPANY_ID, newValue?.value ?? '')}
                options={outsourceLoading ? [] : outsourceCompanies}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                renderInput={(params) => <TextField {...params} label="Outsource Company" autoComplete="off" />}
                loading={outsourceLoading}
                disabled={!facilityId || disabled}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
      {isOutsourced === false && (
        <Grid item {...autocompleteRowProps}>
          <Grid container columnSpacing={2} rowSpacing={1}>
            <Grid item>
              <Autocomplete
                sx={{ minWidth: 300 }}
                size="small"
                value={vehicles?.find((c) => c.value === vehicleId) ?? null}
                onChange={(_, newValue) => setter(FIELDS.VEHICLE_ID, newValue?.value ?? '')}
                options={vehiclesLoading ? [] : vehicles}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                renderInput={(params) => <TextField {...params} label="Vehicle" autoComplete="off" />}
                loading={vehiclesLoading}
                disabled={!facilityId || disabled}
              />
            </Grid>
            <Grid item>
              <Autocomplete
                sx={{ minWidth: 300 }}
                size="small"
                value={drivers?.find((c) => c.value === assignedDriverId) ?? null}
                onChange={(_, newValue) => setter(FIELDS.ASSIGNED_DRIVER_ID, newValue?.value ?? '')}
                options={driversLoading ? [] : drivers}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                renderInput={(params) => <TextField {...params} label="Driver" autoComplete="off" />}
                loading={driversLoading}
                disabled={!facilityId || disabled}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
}
